import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab, CircularProgress } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import Editor from '@monaco-editor/react';

function JavaScriptWorkspace({ question, onCodeChange, initialCode = '' }) {
  const [code, setCode] = useState(initialCode || '// Write your JavaScript code here\nconsole.log("Hello, JavaScript!");\n');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange(code);
    }
  }, [code, onCodeChange]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Configure JavaScript language features
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      allowJs: true,
      checkJs: false,
    });
  };

  const runCode = async () => {
    setIsRunning(true);
    setOutput('Running JavaScript...\n');
    setActiveTab(1);

    try {
      // Capture console output
      const logs = [];
      const errors = [];

      const originalLog = console.log;
      const originalError = console.error;
      const originalWarn = console.warn;

      console.log = (...args) => {
        logs.push(args.join(' '));
        originalLog(...args);
      };

      console.error = (...args) => {
        errors.push('ERROR: ' + args.join(' '));
        originalError(...args);
      };

      console.warn = (...args) => {
        errors.push('WARNING: ' + args.join(' '));
        originalWarn(...args);
      };

      try {
        // Execute JavaScript code
        eval(code);

        // Restore console
        console.log = originalLog;
        console.error = originalError;
        console.warn = originalWarn;

        let result = '--- Output ---\n';
        if (logs.length > 0) {
          result += logs.join('\n');
        } else {
          result += '(No output)';
        }

        if (errors.length > 0) {
          result += '\n\n--- Errors/Warnings ---\n' + errors.join('\n');
        }

        setOutput(result);
      } catch (execError) {
        console.log = originalLog;
        console.error = originalError;
        console.warn = originalWarn;

        setOutput('--- Runtime Error ---\n' + execError.toString() + '\n\nStack:\n' + (execError.stack || ''));
      }
    } catch (error) {
      setOutput('Execution error: ' + error.message);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Question Display */}
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'JavaScript Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="JavaScript Editor" />
        <Tab label="Console Output" />
      </Tabs>

      {/* Editor Tab */}
      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="javascript"
            value={code}
            onChange={(value) => setCode(value || '')}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              roundedSelection: false,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {/* Output Tab */}
      {activeTab === 1 && (
        <Paper
          variant="outlined"
          sx={{
            flexGrow: 1,
            p: 2,
            bgcolor: '#1e1e1e',
            color: '#d4d4d4',
            fontFamily: 'monospace',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {output || 'Click "Run Code" to see console output here...'}
        </Paper>
      )}

      {/* Run Button */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
          onClick={runCode}
          disabled={isRunning}
        >
          {isRunning ? 'Running...' : 'Run Code'}
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>JavaScript Workspace</strong> - Write and execute modern JavaScript (ES2020+).
        Use console.log() to see output.
      </Alert>
    </Box>
  );
}

export default JavaScriptWorkspace;
